import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faFire, faTrophy, faMedal, faRobot, faBolt, faHeartPulse, faStopwatch, faCrown, faHandFist, faArrowRight, faMobileScreen, faDownload, faBed, faSuitcase } from '@fortawesome/free-solid-svg-icons';
import logo from '../assets/logo.png';
import Footer from '../components/Footer';
import { getEloColor } from '../utils/ladder';
import { useInstallPrompt } from '../hooks/useInstallPrompt';
import '../styles/Login.css';

import { API_BASE } from '../config';
const STRAVA_AUTH_URL = `${API_BASE}/auth/strava?return_url=${encodeURIComponent(window.location.href.split('?')[0])}`;

interface LeaderboardPlayer {
  id: number;
  display_name: string;
  avatar_url: string;
  is_bot: boolean;
  elo_rating: number;
  elo_games: number;
  wins: number;
  losses: number;
  avg_weekly_points: number;
}

export default function LoginPage() {
  const [loading, setLoading] = useState(false);
  const [topPlayers, setTopPlayers] = useState<LeaderboardPlayer[]>([]);
  const [leaderboardLoaded, setLeaderboardLoaded] = useState(false);
  const { showInstallBanner, promptInstall, isIOS } = useInstallPrompt();
  const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

  useEffect(() => {
    fetch(`${API_BASE}/public/leaderboard`)
      .then(r => r.json())
      .then(data => {
        if (data?.ranking) setTopPlayers(data.ranking);
        setLeaderboardLoaded(true);
      })
      .catch(() => setLeaderboardLoaded(true));
  }, []);

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setLoading(true);
    setTimeout(() => {
      window.location.href = STRAVA_AUTH_URL;
    }, 300);
  };

  return (
    <div className="landing-page">
      {/* ── Header: Nur Logo ── */}
      <header className="landing-header">
        <img src={logo} alt="NoWeakWeek" className="landing-logo" />
      </header>

      {/* ── Hero ── */}
      <section className="landing-hero">
        <div className="landing-hero-content">
          <h1 className="landing-title">
            <FontAwesomeIcon icon={faFire} className="landing-fire-icon" />
            NoWeakWeek
          </h1>
          <p className="landing-subtitle">
            Compete against an opponent every week – who trains the hardest?
          </p>
          <p className="landing-description">
            Connect your Strava account, get matched with an opponent, and prove who's the stronger athlete. 
            Every workout counts – earn points, climb the leaderboard, and become the champion of the week.
          </p>
          {loading ? (
            <div className="btn-strava btn-strava--loading">
              <FontAwesomeIcon icon={faSpinner} className="fa-spin" />
              <span>Connecting to Strava…</span>
            </div>
          ) : (
            <a href={STRAVA_AUTH_URL} className="btn-strava" onClick={handleClick}>
              <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M15.387 17.944l-2.089-4.116h-3.065L15.387 24l5.15-10.172h-3.066m-7.008-5.599l2.836 5.598h4.172L10.463 0l-7 13.828h4.169"/></svg>
              Sign in with Strava
            </a>
          )}
        </div>
      </section>

      {/* ── How it works ── */}
      <section className="landing-section">
        <h2 className="landing-section-title">How it works</h2>
        <div className="landing-steps">
          <div className="landing-step">
            <div className="landing-step-number">1</div>
            <div className="landing-step-icon"><FontAwesomeIcon icon={faBolt} /></div>
            <h3>Connect Strava</h3>
            <p>Link your Strava account – we'll sync your workouts automatically.</p>
          </div>
          <div className="landing-step">
            <div className="landing-step-number">2</div>
            <div className="landing-step-icon"><FontAwesomeIcon icon={faHandFist} /></div>
            <h3>Get an Opponent</h3>
            <p>Every week you're matched with another athlete. The duel begins!</p>
          </div>
          <div className="landing-step">
            <div className="landing-step-number">3</div>
            <div className="landing-step-icon"><FontAwesomeIcon icon={faHeartPulse} /></div>
            <h3>Train Hard</h3>
            <p>Every workout earns points – the harder you push, the more you score.</p>
          </div>
          <div className="landing-step">
            <div className="landing-step-number">4</div>
            <div className="landing-step-icon"><FontAwesomeIcon icon={faTrophy} /></div>
            <h3>Win the Duel</h3>
            <p>Outscore your opponent and climb the leaderboard. Who's the strongest?</p>
          </div>
        </div>
      </section>

      {/* ── Features ── */}
      <section className="landing-section landing-section--features">
        <h2 className="landing-section-title">Features</h2>
        <div className="landing-features">
          <div className="landing-feature">
            <div className="landing-feature-icon"><FontAwesomeIcon icon={faFire} /></div>
            <h3>Weekly Duels</h3>
            <p>Compete head-to-head every week. The athlete with the most points wins.</p>
          </div>
          <div className="landing-feature">
            <div className="landing-feature-icon"><FontAwesomeIcon icon={faBolt} /></div>
            <h3>Smart Scoring</h3>
            <p>Points based on heart rate zones, duration, and intensity. Every minute counts.</p>
          </div>
          <div className="landing-feature">
            <div className="landing-feature-icon"><FontAwesomeIcon icon={faTrophy} /></div>
            <h3>Leaderboard</h3>
            <p>Climb the ranks and prove you're the strongest athlete in the community.</p>
          </div>
          <div className="landing-feature">
            <div className="landing-feature-icon"><FontAwesomeIcon icon={faCrown} /></div>
            <h3>Daily Challenges</h3>
            <p>Special challenges every day – earn bonus points and unlock achievements.</p>
          </div>
          <div className="landing-feature">
            <div className="landing-feature-icon"><FontAwesomeIcon icon={faBed} /></div>
            <h3>Items</h3>
            <p>Earn items after each duel – use them to boost your score or reveal opponent activities.</p>
          </div>
          <div className="landing-feature">
            <div className="landing-feature-icon"><FontAwesomeIcon icon={faSuitcase} /></div>
            <h3>Badges</h3>
            <p>Unlock badges by reaching milestones – they grant weekly bonus points.</p>
          </div>
        </div>
      </section>

      {/* Leaderboard Preview removed */}

      {/* ── CTA ── */}
      <section className="landing-section landing-section--cta">
        <h2 className="landing-section-title">Ready to compete?</h2>
        <p className="landing-cta-text">Join the community and start your first duel today.</p>
        {loading ? (
          <div className="btn-strava btn-strava--loading">
            <FontAwesomeIcon icon={faSpinner} className="fa-spin" />
            <span>Connecting to Strava…</span>
          </div>
        ) : (
          <a href={STRAVA_AUTH_URL} className="btn-strava" onClick={handleClick}>
            <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M15.387 17.944l-2.089-4.116h-3.065L15.387 24l5.15-10.172h-3.066m-7.008-5.599l2.836 5.598h4.172L10.463 0l-7 13.828h4.169"/></svg>
            Sign in with Strava
          </a>
        )}
      </section>

      {/* ── Install the App ── (Mobile only) */}
      {isMobile && (
        <section className="landing-section landing-section--install">
          <div className="landing-install-card">
            <div className="landing-install-icon">
              <FontAwesomeIcon icon={faMobileScreen} />
            </div>
            <h3 className="landing-install-title">Get the App</h3>
            <p className="landing-install-text">
              {isIOS
                ? 'Tap the Share button in Safari, then "Add to Home Screen" to install NoWeakWeek.'
                : 'Install NoWeakWeek on your phone for the best experience.'}
            </p>
            {isIOS ? (
              <div className="landing-install-steps">
                <div className="landing-install-step">
                  <span className="landing-install-step-num">1</span>
                  <span>Tap the <strong>Share</strong> button in Safari</span>
                </div>
                <div className="landing-install-step">
                  <span className="landing-install-step-num">2</span>
                  <span>Tap <strong>"Add to Home Screen"</strong></span>
                </div>
                <div className="landing-install-step">
                  <span className="landing-install-step-num">3</span>
                  <span>Tap <strong>"Add"</strong> in the top right</span>
                </div>
              </div>
            ) : (
              <button className="landing-install-btn" onClick={promptInstall}>
                <FontAwesomeIcon icon={faDownload} />
                Install App
              </button>
            )}
          </div>
        </section>
      )}

      {/* ── Footer ── */}
      <Footer />
    </div>
  );
}
